import type { Player } from '@schibsted-svp/web-player';

import type { DeviceType } from '../types';

type StartMode = 'silent' | 'auto' | 'manual';

function getStartMode(
  autoplay: boolean | 'viewable',
  mute: boolean
): StartMode {
  if (autoplay && mute) {
    return 'silent';
  }

  if (autoplay) {
    return 'auto';
  }

  return 'manual';
}

function getVideoPlaybackMethod(startMode: StartMode) {
  switch (startMode) {
    case 'auto':
      // autoplay, sound on
      return 1;
    case 'silent':
      // autoplay, sound off
      return 2;
    default:
      // click to play - default
      return 3;
  }
}

/**
 * @returns {'ios'|'android'|'unknown'}
 */
function getMobileOS() {
  const containsIPhone = navigator.userAgent.match(/iphone/i);
  const containsAndroid = navigator.userAgent.match(/android/i);

  if (containsIPhone && !containsAndroid) {
    return 'ios';
  }

  if (containsAndroid && !containsIPhone) {
    return 'android';
  }

  return 'unknown';
}

const videoPlaybackMethodMap = new Map<number, string>([
  [1, 'autoplay'],
  [2, 'muted-autoplay'],
  [3, 'click-to-play'],
]);

/**
 * Returns a set of environment related keywords. These are not used for targeting, but instead for reporting and debugging issues.
 */
function getEnvironmentKeywords(player: Player, deviceType: DeviceType) {
  const config = player.getRawConfig();
  const { mute, autoplay } = config;

  const startMode = getStartMode(autoplay!, mute!);
  const playbackMethod = getVideoPlaybackMethod(startMode);

  return {
    'kw_se-generic-mobile-os': getMobileOS(),
    'kw_se-generic-sitemode': deviceType.replace('phone', 'mobile'),
    'kw_se-aftonbladet-navigation': 'spa',
    'kw_se-generic-appmode': 'notinapp',
    'kw_se-generic-screenheight': window.screen.height.toString(),
    'kw_se-generic-screenwidth': window.screen.width.toString(),
    'kw_se-generic-viewportheight': window.innerHeight.toString(),
    'kw_se-generic-viewportwidth': window.innerWidth.toString(),
    'kw_se-generic-playback-method': videoPlaybackMethodMap.get(playbackMethod),
    'vskippable': '0',
    'vplaybackmethod': playbackMethod,
    'referrer': document.location.href,
  };
}

export { getEnvironmentKeywords };
